import { cn } from '@/lib/utils'

/**
 * Canonical marketing section: one place for the section rhythm and content
 * width that pages previously hand-rolled as `container mx-auto px-4` plus
 * ad-hoc `max-w-*` / `py-*` combinations.
 *
 * Deliberately small API — this is a layout convention, not a layout engine.
 */
interface SectionProps {
  /** Inner content width. default = product sections, narrow = editorial copy,
   *  wide = dense grids, hero = wider opening frame,
   *  full = no inner max-width (rare). */
  width?: 'default' | 'narrow' | 'wide' | 'hero' | 'full'
  /** Vertical rhythm. default = standard section, compact = stacked/dense,
   *  none = parent controls spacing. */
  rhythm?: 'default' | 'compact' | 'none'
  /** Render element. Use 'div' when nesting inside another <section>. */
  as?: 'section' | 'div'
  id?: string
  className?: string
  /** Classes for the width-constrained inner wrapper. */
  innerClassName?: string
  children: React.ReactNode
}

const WIDTHS: Record<NonNullable<SectionProps['width']>, string> = {
  narrow: 'max-w-3xl mx-auto',
  default: 'max-w-5xl mx-auto',
  wide: 'max-w-7xl mx-auto',
  hero: '',
  full: '',
}

// Vertical rhythm, widened 2026-08-08 to match the approved design
// references, which run 112-118px of section padding on desktop. The old
// values (64/80px) read as cramped against those layouts — this is the one
// lever that fixes section breathing room across every marketing page.
const RHYTHMS: Record<NonNullable<SectionProps['rhythm']>, string> = {
  default: 'py-20 md:py-28',
  compact: 'py-12 md:py-16',
  none: '',
}

export function Section({
  width = 'default',
  rhythm = 'default',
  as: Tag = 'section',
  id,
  className,
  innerClassName,
  children,
}: SectionProps) {
  return (
    <Tag id={id} className={cn(RHYTHMS[rhythm], className)}>
      <div
        className={
          width === 'hero'
            ? 'max-w-hero mx-auto px-4 min-[1344px]:px-8'
            : 'max-w-shell mx-auto px-4'
        }
      >
        <div className={cn(WIDTHS[width], innerClassName)}>{children}</div>
      </div>
    </Tag>
  )
}

export default Section
